import { computeMd5 } from './md5Hash';

interface Md5WorkerReadyMessage {
  type: 'ready';
}

interface Md5WorkerResultMessage {
  type: 'result';
  requestId: number;
  hash: string;
  buffer: ArrayBuffer;
}

interface Md5WorkerErrorMessage {
  type: 'error';
  requestId: number;
  error: string;
  buffer?: ArrayBuffer;
}

type Md5WorkerResponse = Md5WorkerReadyMessage | Md5WorkerResultMessage | Md5WorkerErrorMessage;

interface Md5Result {
  hash: string;
  buffer: ArrayBuffer;
}

interface PendingRequest {
  resolve: (result: Md5Result) => void;
  reject: (error: unknown) => void;
}

async function computeMd5Sync(buffer: ArrayBuffer): Promise<Md5Result> {
  return {
    hash: computeMd5(buffer),
    buffer,
  };
}

export class Md5WorkerClient {
  private md5Worker?: Worker;

  private md5WorkerReadyPromise?: Promise<Worker>;

  private nextRequestId = 1;

  private lastRequestPromise = Promise.resolve();

  private pendingRequests = new Map<number, PendingRequest>();

  private terminateMd5Worker() {
    if (this.md5Worker) {
      this.md5Worker.terminate();
    }
    this.md5Worker = undefined;
    this.md5WorkerReadyPromise = undefined;
  }

  private rejectPendingRequests(error: unknown) {
    this.pendingRequests.forEach(({ reject }) => reject(error));
    this.pendingRequests.clear();
  }

  private async ensureMd5Worker(): Promise<Worker> {
    if (this.md5WorkerReadyPromise) {
      return this.md5WorkerReadyPromise;
    }

    let worker: Worker;

    try {
      worker = new Worker(
        new URL(/* webpackChunkName: "Md5HashWorker" */ 'src/utils/md5Hash.worker', import.meta.url)
      );
    } catch (error) {
      this.terminateMd5Worker();
      throw error;
    }

    this.md5Worker = worker;

    this.md5WorkerReadyPromise = new Promise<Worker>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<Md5WorkerResponse>) => {
        const { data } = event;

        if (data.type === 'ready') {
          resolve(worker);
          return;
        }

        const pendingRequest = this.pendingRequests.get(data.requestId);
        if (!pendingRequest) {
          return;
        }

        this.pendingRequests.delete(data.requestId);

        if (data.type === 'result') {
          pendingRequest.resolve({ hash: data.hash, buffer: data.buffer });
          return;
        }

        if (data.buffer) {
          computeMd5Sync(data.buffer).then(pendingRequest.resolve, pendingRequest.reject);
          return;
        }

        pendingRequest.reject(new Error(data.error));
      };

      worker.onerror = (event) => {
        const error =
          event instanceof ErrorEvent
            ? (event.error ?? new Error(event.message || 'MD5 worker error'))
            : new Error('MD5 worker error');

        reject(error);
        this.rejectPendingRequests(error);
        this.terminateMd5Worker();
      };
    });

    return this.md5WorkerReadyPromise;
  }

  private async computeMd5Once(buffer: ArrayBuffer): Promise<Md5Result> {
    if (typeof Worker === 'undefined') {
      return computeMd5Sync(buffer);
    }

    let worker: Worker;

    try {
      worker = await this.ensureMd5Worker();
    } catch {
      return computeMd5Sync(buffer);
    }

    return new Promise<Md5Result>((resolve, reject) => {
      const requestId = this.nextRequestId++;
      this.pendingRequests.set(requestId, { resolve, reject });

      try {
        worker.postMessage({ type: 'compute', requestId, buffer }, [buffer]);
      } catch {
        this.pendingRequests.delete(requestId);
        computeMd5Sync(buffer).then(resolve, reject);
      }
    });
  }

  public compute(buffer: ArrayBuffer): Promise<Md5Result> {
    const nextRequest = this.lastRequestPromise.then(() => this.computeMd5Once(buffer));
    this.lastRequestPromise = nextRequest.then(
      () => undefined,
      () => undefined
    );
    return nextRequest;
  }

  // test cleanup용
  public dispose() {
    this.rejectPendingRequests(new Error('MD5 worker client disposed'));
    this.terminateMd5Worker();
    this.nextRequestId = 1;
    this.lastRequestPromise = Promise.resolve();
  }
}

const md5WorkerClient = new Md5WorkerClient();

export function computeMd5InWorker(buffer: ArrayBuffer): Promise<Md5Result> {
  return md5WorkerClient.compute(buffer);
}
